import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import printJS from 'print-js';
import CustomItemPopup from './CustomItemPopup.js';
import { generateExchangeBill } from '../Api/exchangeBillApi';
import { API_BASE_URL } from '../Config.js';
import { BILL_FETCH_URL, DEFECT_ITEM_RETURN } from '../constant/apiConstants.js';
import BillPopup from './BillPopup';
import PrintBillModal from '../print/PrintBillModal.js';
import styles from './ReturnExchangePop.module.css';

const ReturnExchangePop = ({ onClose, userData, initialBillNo = '' }) => {
  /* ----------------------------------------------------------------
   * View control: 'bill' shows the fetched bill / return screen,
   * 'exchange' shows the (formerly separate) exchange-bill screen
   * inline inside the SAME popup. No redirect to another component.
   * ---------------------------------------------------------------- */
  const [currentView, setCurrentView] = useState('bill');

  /* -------------------- Bill / return state -------------------- */
  const [billNo, setBillNo] = useState(initialBillNo);
  const [billData, setBillData] = useState(null);
  const [returnedItems, setReturnedItems] = useState({});
  const [currentDate] = useState(new Date().toISOString().split('T')[0]);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [selectedItems, setSelectedItems] = useState([]); // items chosen from the original bill
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [defectItem, setDefectItem] = useState(null);
  const [defectQuantity, setDefectQuantity] = useState(1);
  const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);
  const [billList, setBillList] = useState([]);
  const [showBillSelectionPopup, setShowBillSelectionPopup] = useState(false);

  /* -------------------- Exchange state -------------------- */
  const [itemsToExchange, setItemsToExchange] = useState([]); // returned items being exchanged
  const [exchangeAmount, setExchangeAmount] = useState(0);    // credit from returned items
  const [billingItems, setBillingItems] = useState([]);       // NEW items added to exchange bill
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [customerName, setCustomerName] = useState('');
  const [customerMobileNo, setCustomerMobileNo] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [schoolName, setSchoolName] = useState('');
  const [allSchools, setAllSchools] = useState([]);
  const [isBarcodeMode, setIsBarcodeMode] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [shiftPressTime, setShiftPressTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTableFocused, setIsTableFocused] = useState(false);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [billInfo, setBillInfo] = useState(null);
  const [customItem, setCustomItem] = useState({
    itemBarcodeID: 'SG9999999',
    itemType: '',
    itemColor: '',
    itemSize: '',
    itemCategory: '',
    price: 0,
    discount: 0,
    sellPrice: 0,
    quantity: 1,
    amount: 0,
  });

  const searchInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRefs = useRef([]);
  const selectedSchoolRef = useRef(null);

  /* ================================================================
   * SHARED HELPERS
   * ================================================================ */
  const resetPage = () => {
    setBillNo('');
    setBillData(null);
    setReturnedItems({});
    setReturnQuantities({});
    setSelectedItems([]);
    setShowPopup(false);
    setPopupMessage('');
    setPopupType('');
    setDefectItem(null);
    setDefectQuantity(1);
    setItemsToExchange([]);
    setBillList([]);
    setShowBillSelectionPopup(false);
    setIsDefectModalOpen(false);
    setExchangeAmount(0);
    setBillingItems([]);
    setCurrentView('bill');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isToday = (date) => date === currentDate;

  const handleInputChange = (event) => setBillNo(event.target.value);

  const safeParse = (raw, fallback = {}) => {
    if (raw == null || raw === 'undefined' || raw === 'null' || raw === '') {
      return fallback;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  };

  const fetchBill = async () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user ? user.storeId : '';
    if (!billNo) {
      resetPage();
      return;
    }
    try {
      const response = await axios.get(`${BILL_FETCH_URL}`, {
        params: { billNo, storeId },
      });
      const data = response.data;

      if (data.type === 'single') {
        setBillData(data.bill);
        setShowBillSelectionPopup(false);
        setPopupMessage('');
        setSelectedItems([]);
      } else if (data.type === 'list') {
        resetPage();
        setReturnedItems({});
        setReturnQuantities({});
        setBillList(data.billList);
        setShowBillSelectionPopup(true);
        setSelectedItems([]);
      } else {
        alert('Bill not found');
        resetPage();
      }
    } catch (error) {
      alert('Error fetching bill data, please try again');
    }
  };

  const handleSelectBill = (bill) => {
    setBillData(bill);
    setShowBillSelectionPopup(false);
  };

  const handleClosePopup = () => setShowPopup(false);

  // When opened from another screen with a bill number, load it immediately.
  useEffect(() => {
    if (initialBillNo) {
      fetchBill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================================================================
   * RETURN / DELETE / DEFECT
   * ================================================================ */
  const handleDeleteBill = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user ? user.storeId : '';

    if (window.confirm('Are you sure you want to delete this bill?')) {
      axios
        .post(`${API_BASE_URL}/user/cancelBill`, null, {
          params: { billNo: billData.billNo, storeId },
        })
        .then((response) => {
          alert(response.data);
          resetPage();
        })
        .catch((error) => {
          console.error('Error deleting bill:', error);
          setPopupMessage('An error occurred. Please try again.');
          setPopupType('error');
          setShowPopup(true);
        });
    }
  };

  const handleReturnQuantityChange = (sno, value) => {
    const parsedValue = parseInt(value, 10);
    if (isNaN(parsedValue) || parsedValue <= 0) {
      setReturnQuantities((prev) => ({ ...prev, [sno]: 0 }));
      return;
    }
    const billItem = billData.bill.find((item) => item.sno === sno);
    if (parsedValue > billItem.quantity) {
      alert('Return quantity cannot exceed available quantity.');
      setReturnQuantities((prev) => ({ ...prev, [sno]: 0 }));
    } else {
      setReturnQuantities((prev) => ({ ...prev, [sno]: parsedValue }));
    }
  };

  const handleSelectItem = (item) => {
    if (!returnedItems[item.sno]) {
      setSelectedItems((prev) => [...prev, item]);
      setReturnQuantities((prev) => ({ ...prev, [item.sno]: 0 }));
    }
  };

  const handleDeselectItem = (item) => {
    setSelectedItems((prev) => prev.filter((s) => s.sno !== item.sno));
    setReturnQuantities((prev) => {
      const next = { ...prev };
      delete next[item.sno];
      return next;
    });
  };

  useEffect(() => {
    selectedItems.forEach((item) => {
      if (!returnQuantities[item.sno] || returnQuantities[item.sno] === 0) {
        handleReturnQuantityChange(item.sno, 1);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const calculateReturnTotal = () =>
    selectedItems.reduce((total, item) => {
      const qty = returnQuantities[item.sno] || 0;
      return total + item.sellPrice * qty;
    }, 0);

  const confirmReturn = async () => {
    const allValid = selectedItems.every((item) => {
      const q = returnQuantities[item.sno];
      return q !== undefined && q > 0;
    });
    if (!allValid) {
      alert('Please enter a valid return quantity for all selected items.');
      return;
    }

    const itemsToReturn = selectedItems.map((item) => ({
      sno: item.sno,
      barcodedId: item.itemBarcodeID,
      price: item.sellPrice,
      userId: userData.userId,
      return_quantity: returnQuantities[item.sno],
    }));

    try {
      const response = await axios.post(`${API_BASE_URL}/user/return_stock/bill`, itemsToReturn);
      if (response.data === 'success') {
        setPopupMessage('Item returned');
        setPopupType('success');
      } else {
        setPopupMessage('Please try again');
        setPopupType('error');
      }
      setShowPopup(true);

      setReturnedItems((prev) => {
        const next = { ...prev };
        itemsToReturn.forEach((item) => {
          next[item.sno] = true;
        });
        return next;
      });

      setSelectedItems([]);
      fetchBill();
    } catch (error) {
      console.error('Error returning items:', error);
      setPopupMessage('Please try again');
      setPopupType('error');
      setShowPopup(true);
    }
  };

  const handleDefectItem = (item) => {
    setDefectItem(item);
    setDefectQuantity(1);
    setIsDefectModalOpen(true);
  };

  const confirmDefect = async () => {
    try {
      const response = await axios.post(`${DEFECT_ITEM_RETURN}`, {
        sno: defectItem.sno,
        barcodedId: defectItem.itemBarcodeID,
        return_quantity: defectQuantity,
        price: defectItem.sellPrice,
        userId: userData.userId,
      });

      if (response.data.status === 'success') {
        setPopupMessage(response.data.message);
        setPopupType('success');
        setIsDefectModalOpen(false);
        setSelectedItems([]);
      } else {
        setPopupMessage(response.data.message);
        setPopupType('failed');
      }
      setShowPopup(true);
    } catch (error) {
      console.error(error);
      setPopupMessage('Error in returning defected item');
      setPopupType('failed');
      setShowPopup(true);
    } finally {
      fetchBill();
    }
  };

  /* ================================================================
   * ENTER EXCHANGE (inline, no redirect)
   * ================================================================ */
  const handleExchange = () => {
    const allValid = selectedItems.every((item) => {
      const q = returnQuantities[item.sno];
      return q !== undefined && q > 0;
    });
    if (!allValid) {
      alert('Please enter a valid return quantity for all selected items.');
      return;
    }

    const exchangeItems = selectedItems.map((item) => ({
      sno: item.sno,
      barcodedId: item.itemBarcodeID,
      price: item.sellPrice,
      itemType: item.itemType,
      billCategory: item.billCategory,
      itemCategory: item.itemCategory,
      userId: userData.userId,
      return_quantity: returnQuantities[item.sno],
    }));

    setItemsToExchange(exchangeItems);
    setExchangeAmount(calculateReturnTotal());
    setSchoolName(billData.schoolName || '');
    setCurrentView('exchange');
  };

  const handleBackToBill = () => {
    setBillingItems([]);
    setCustomerName('');
    setCustomerMobileNo('');
    setPaymentMode('Cash');
    setExchangeAmount(0);
    setItemsToExchange([]);
    setSelectedItems([]);
    setCurrentView('bill');
    fetchBill();
  };

  /* ================================================================
   * EXCHANGE: schools, search, barcode, item table
   * ================================================================ */
  const fetchAllSchools = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;
      const response = await axios.get(`${API_BASE_URL}/user/filter/getSchoolNameandCode`, {
        params: { storeId },
      });
      if (Array.isArray(response.data)) {
        setAllSchools(
          response.data.map((school) => ({
            value: `${school.schoolName} (${school.schoolCode})`,
            label: `${school.schoolName} (${school.schoolCode})`,
            schoolName: school.schoolName,
            schoolCode: school.schoolCode,
          }))
        );
      } else {
        console.error('Expected an array of schools, but got:', response.data);
      }
    } catch (error) {
      console.error('Error fetching school names:', error);
    }
  };

  // Load schools once we move into the exchange view.
  useEffect(() => {
    if (currentView === 'exchange') {
      fetchAllSchools();
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [currentView]);

  const handleSchoolChange = (option) => {
    setSchoolName(option ? option.schoolName : '');
  };

  // Search by item code
  useEffect(() => {
    if (currentView !== 'exchange') return;
    if (searchTerm.trim() === '') return;

    const fetchItems = async () => {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        const storeId = user ? user.storeId : '';
        const response = await axios.get(`${API_BASE_URL}/inventory/getAllItems`, {
          params: { searchTerm, storeId },
        });
        setSearchResults(response.data || []);
        setSelectedIndex(-1);
        setDropdownOpen(true);
      } catch (error) {
        console.error('Error fetching items:', error);
        setSearchResults([]);
      }
    };
    fetchItems();
  }, [searchTerm, currentView]);

  // Barcode scan
  useEffect(() => {
    if (currentView !== 'exchange') return;
    if (!isBarcodeMode || barcode.trim() === '') return;

    const fetchItemByBarcode = async () => {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        const storeId = user?.storeId;
        const response = await axios.get(`${API_BASE_URL}/user/search/item_code`, {
          params: { barcode: barcode.trim(), storeId },
        });
        if (response.data) {
          addItemToBill(response.data);
        } else {
          console.error('Item not found for barcode:', barcode);
        }
      } catch (error) {
        console.error('Error fetching item by barcode:', error);
      } finally {
        setBarcode('');
        barcodeInputRef.current?.focus();
      }
    };
    fetchItemByBarcode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode, isBarcodeMode, currentView]);

  const addItemToBill = (item) => {
    const idx = billingItems.findIndex((b) => b.itemBarcodeID === item.itemBarcodeID);
    if (idx > -1) {
      const updated = [...billingItems];
      const existing = updated[idx];
      existing.quantity += 1;
      existing.amount = existing.quantity * existing.price;
      setBillingItems(updated);
    } else {
      setBillingItems([...billingItems, { ...item, quantity: 1, amount: item.price * 1 }]);
    }

    setSearchTerm('');
    setSearchResults([]);
    setDropdownOpen(false);

    if (!isBarcodeMode) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  };

  const removeItemFromBill = (index) => {
    const updated = [...billingItems];
    updated.splice(index, 1);
    setBillingItems(updated);
  };

  const handleBillingQuantityChange = (index, quantity) => {
    const qty = parseInt(quantity, 10) || 1;
    const updated = [...billingItems];
    updated[index] = {
      ...updated[index],
      quantity: qty,
      amount: qty * updated[index].price,
    };
    setBillingItems(updated);
  };

  const calculateBillingTotal = () => billingItems.reduce((t, i) => t + i.amount, 0);
  const calculateBillingQuantity = () => billingItems.reduce((t, i) => t + i.quantity, 0);

  const handleArrowNavigation = (e) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      addItemToBill(searchResults[selectedIndex]);
      e.preventDefault();
    }
  };

  const handleDropdownRowKeyDown = (event, item) => {
    if (event.key === 'Enter') addItemToBill(item);
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep highlighted dropdown row in view
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const dropdown = dropdownRef.current;
      const items = dropdown.querySelectorAll('tr');
      if (items.length > 0 && items[selectedIndex]) {
        const item = items[selectedIndex];
        const dropdownRect = dropdown.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const itemTop = itemRect.top - dropdownRect.top;
        const itemBottom = itemRect.bottom - dropdownRect.top;
        const visibleHeight = 150;
        if (itemTop < 0) dropdown.scrollTop += itemTop - 15;
        else if (itemBottom > visibleHeight) dropdown.scrollTop += itemBottom - visibleHeight + 15;
      }
    }
  }, [selectedIndex]);

  /* -------------------- Keyboard shortcuts (exchange only) -------------------- */
  // Ctrl+S -> confirm + submit
  useEffect(() => {
    const onKey = (e) => {
      if (currentView !== 'exchange' || showCustomItemModal) return;
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        setShowConfirmPopup(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentView, showCustomItemModal]);

  // Ctrl+C -> custom item modal
  useEffect(() => {
    const onKey = (e) => {
      if (currentView !== 'exchange' || showCustomItemModal) return;
      if (e.ctrlKey && e.key === 'c') setShowCustomItemModal(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentView, showCustomItemModal]);

  // Double-Shift -> toggle barcode mode
  useEffect(() => {
    const onKey = (e) => {
      if (currentView !== 'exchange' || showCustomItemModal) return;
      if (e.key === 'Shift') {
        const now = new Date().getTime();
        if (shiftPressTime && now - shiftPressTime < 500) {
          toggleBarcodeMode();
          setShiftPressTime(null);
        } else {
          setShiftPressTime(now);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftPressTime, currentView, showCustomItemModal]);

  /* While the custom-item modal is open, stop Shift / Ctrl+S / Ctrl+C from
   * reaching the parent screen (NewBillContainer) or this component. The
   * listener runs in the capture phase so it intercepts the key before any
   * window-level handler in either component can react — but it only touches
   * those specific keys, so normal typing inside the modal is unaffected. */
  useEffect(() => {
    if (!showCustomItemModal) return undefined;
    const blockParentShortcuts = (e) => {
      const isShift = e.key === 'Shift';
      const isCtrlCombo = e.ctrlKey && (e.key === 's' || e.key === 'c');
      if (isShift || isCtrlCombo) {
        e.stopPropagation();
        if (isCtrlCombo) e.preventDefault();
      }
    };
    window.addEventListener('keydown', blockParentShortcuts, true);
    return () => window.removeEventListener('keydown', blockParentShortcuts, true);
  }, [showCustomItemModal]);

  const toggleBarcodeMode = () => {
    setIsBarcodeMode((prev) => {
      const next = !prev;
      if (next) setTimeout(() => barcodeInputRef.current?.focus(), 0);
      else setTimeout(() => searchInputRef.current?.focus(), 0);
      return next;
    });
  };

  /* -------------------- Custom item -------------------- */
  useEffect(() => {
    if (showCustomItemModal) {
      setCustomItem({
        itemBarcodeID: 'SG9999999',
        itemType: '',
        itemColor: '',
        itemSize: '',
        itemCategory: '',
        price: 0,
        discount: 0,
        sellPrice: 0,
        quantity: 1,
        amount: 0,
      });
    }
  }, [showCustomItemModal]);

  const handleCustomItemChange = (e) => {
    const { name, value } = e.target;
    setCustomItem((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'price' || name === 'discount') {
        const price = parseInt(updated.price) || 0;
        const discount = parseInt(updated.discount) || 0;
        updated.sellPrice = (price - (price * discount) / 100).toFixed(2);
      }
      if (name === 'quantity' || name === 'sellPrice' || name === 'price' || name === 'discount') {
        const quantity = parseInt(updated.quantity) || 1;
        updated.amount = (parseInt(updated.sellPrice) * quantity).toFixed(2);
      }
      return updated;
    });
  };

  const handleAddCustomItem = () => {
    const quantity = parseInt(customItem.quantity) || 1;
    const price = parseInt(customItem.sellPrice) || 0;
    const newItem = {
      itemBarcodeID: customItem.itemBarcodeID,
      itemCode: customItem.itemBarcodeID,
      itemType: customItem.itemType,
      itemColor: customItem.itemColor,
      itemSize: customItem.itemSize,
      itemCategory: customItem.itemCategory || '',
      itemName: `${customItem.itemCategory || ''} ${customItem.itemType}`.trim(),
      description: `${customItem.itemCategory || ''} ${customItem.itemType} ${customItem.itemSize}`.trim(),
      quantity,
      price,
      amount: quantity * price,
    };
    setBillingItems([...billingItems, newItem]);
    setShowCustomItemModal(false);
  };

  
  /* ================================================================
   * EXCHANGE SUBMIT (retail only)
   * ================================================================ */
const handleExchangeSubmit = async () => {
  if (loading) return;

  const user = JSON.parse(sessionStorage.getItem('user'));

  if (!schoolName) {
    alert('School Name is required');
    return;
  }

  try {
    setLoading(true);

    const storeConfig = safeParse(sessionStorage.getItem('storeConfig'), {});
    const printType = storeConfig.printType || 'PDF';

    const { response, isThermal } = await generateExchangeBill({
      billNo: billData.billNo,
      itemsToExchange,
      selectedItems: billingItems,
      user,
      printType,
    });

    if (isThermal) {
      const billPrintData = response.data;
      if (!billPrintData || typeof billPrintData !== 'object') {
        console.error('Invalid thermal print data:', billPrintData);
        alert('Error: Invalid thermal print data received');
        return;
      }
      setBillInfo(billPrintData);
      setShowPrintModal(true);
    } else {
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      printJS({ printable: pdfUrl, type: 'pdf' });
    }

    setBillingItems([]);
    setCustomerName('');
    setCustomerMobileNo('');
    setPaymentMode('Cash');
    handleBackToBill();
  } catch (error) {
    console.error('Error generating bill:', error);
    alert('Error generating bill. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const handleConfirmSubmit = () => {
    setShowConfirmPopup(false);
    handleExchangeSubmit();
  };

  /* ================================================================
   * SMALL PRESENTATIONAL COMPONENTS
   * ================================================================ */
  const Popup = ({ message, type, onClose: onPopupClose }) => (
    <div className={`${styles.popup} ${styles[type] || ''}`}>
      <div className={styles.popupContent}>
        <p>{message}</p>
        <button onClick={onPopupClose}>Close</button>
      </div>
    </div>
  );

  const BillSelectionPopup = ({ list, onClose: onPopupClose, onSelectBill }) => (
    <div className={styles.billSelectionPopup}>
      <h3>Select a Bill</h3>
      <table>
        <thead>
          <tr>
            <th>Bill No</th>
            <th>Store ID</th>
            <th>User ID</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map((bill) => (
            <tr key={bill.billNo}>
              <td>{bill.billNo}</td>
              <td>{bill.storeId}</td>
              <td>{bill.userId}</td>
              <td>{bill.bill_date}</td>
              <td>
                <button onClick={() => onSelectBill(bill)}>View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={onPopupClose}>Close</button>
    </div>
  );

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: '300px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      '&:hover': { borderColor: '#3b82f6' },
      '&:focus-within': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    }),
  };

  /* ================================================================
   * RENDER
   * ================================================================ */
  return (
    <div className={styles.billDetailsPopupOverlay}>
      <div className={styles.billDetailsPopup}>
        <div className={styles.billDetailsPopupHeader}>
          <h3>{currentView === 'exchange' ? 'Exchange Bill' : 'Return / Exchange'}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.billDetailsPopupContent}>
          {/* ============================================================
           *  VIEW 1: BILL / RETURN
           * ============================================================ */}
          {currentView === 'bill' && (
            <>
              <div className={styles.billSearchSection}>
                <label htmlFor="billNo">Enter Bill No:</label>
                <input
                  type="text"
                  id="billNo"
                  value={billNo}
                  onChange={handleInputChange}
                  placeholder="Enter bill no or mobile no"
                />
                <button onClick={fetchBill}>Fetch Bill</button>
                <button className={styles.billDetailsPopupBtn} onClick={resetPage}>
                  Clear
                </button>
              </div>

              {billData && (
                <>
                  <div className={styles.billDetailInfo}>
                    <table className={styles.billdetsInfo}>
                      <tbody>
                        <tr>
                          <td>
                            <p>
                              <strong>Bill No:</strong> {billData.billNo}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Cashier:</strong> {billData.userId}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Bill Date:</strong> {formatDate(billData.bill_date)}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Status</strong> {billData.status}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p>
                              <strong>Customer Name:</strong> {billData.customerName || 'N/A'}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Payment Mode:</strong> {billData.paymentMode}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Final Amount:</strong> {billData.final_amount}
                            </p>
                          </td>
                          <td>
                            <p>
                              <strong>Bill Type</strong> {billData.billType}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.billDetailWrapper}>
                    <table className={styles.billDetailTable}>
                      <thead>
                        <tr>
                          <th>Sno</th>
                          <th>Barcode ID</th>
                          <th>Description</th>
                          <th>Item Type</th>
                          <th>Color</th>
                          <th>Size</th>
                          <th>Item Category</th>
                          <th>Available Quantity</th>
                          <th>Return Quantity</th>
                          <th>Sell Price</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billData.bill.map((item) => {
                          const isSelected = selectedItems.some((s) => s.sno === item.sno);
                          return (
                            <tr
                              key={item.sno}
                              className={isSelected ? styles.rowSelected : ''}
                            >
                              <td>{item.sno}</td>
                              <td>{item.itemBarcodeID}</td>
                              <td>{item.description}</td>
                              <td>{item.itemType}</td>
                              <td>{item.itemColor}</td>
                              <td>{item.itemSize}</td>
                              <td>{item.itemCategory}</td>
                              <td>{item.quantity}</td>
                              <td>
                                {isSelected ? (
                                  <div className={styles.itemTableQuantityContainer}>
                                    <button
                                      type="button"
                                      className={`${styles.itemTableQuantityBtn} ${styles.itemTableQuantityBtnDecrease}`}
                                      onClick={() => {
                                        const cur = returnQuantities[item.sno] || 1;
                                        handleReturnQuantityChange(item.sno, Math.max(1, cur - 1));
                                      }}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      className={styles.itemTableQuantityInput}
                                      min="1"
                                      max={item.quantity}
                                      value={returnQuantities[item.sno] || 1}
                                      onChange={(e) => {
                                        let value = parseInt(e.target.value, 10) || 1;
                                        value = Math.max(1, Math.min(item.quantity, value));
                                        handleReturnQuantityChange(item.sno, value);
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className={`${styles.itemTableQuantityBtn} ${styles.itemTableQuantityBtnIncrease}`}
                                      onClick={() => {
                                        const cur = returnQuantities[item.sno] || 1;
                                        handleReturnQuantityChange(
                                          item.sno,
                                          Math.min(item.quantity, cur + 1)
                                        );
                                      }}
                                    >
                                      +
                                    </button>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td>{item.sellPrice}</td>
                              <td>{item.total_amount}</td>
                              <td>
                                {returnedItems[item.sno] ? (
                                  <button disabled>Item Returned</button>
                                ) : item.status === 'RETURN' ? (
                                  <span>Item Returned</span>
                                ) : item.status === 'EXCHANGE' ? (
                                  <span>Item Exchanged</span>
                                ) : item.status === 'DEFECT' ? (
                                  <span>Defected Item Returned</span>
                                ) : item.quantity <= 0 ? (
                                  <span>Item Exchanged or returned</span>
                                ) : (
                                  <div className={styles.returnExchangeCheckbox}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) =>
                                        e.target.checked
                                          ? handleSelectItem(item)
                                          : handleDeselectItem(item)
                                      }
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {selectedItems.length > 0 && (
                      <div className={styles.returnSummary}>
                        <p>
                          <strong>Total Return Amount:</strong> {calculateReturnTotal()}
                        </p>
                        <div className={styles.returnSummaryActions}>
                          <button onClick={confirmReturn}>Return</button>
                          <button onClick={handleExchange}>Exchange</button>
                          <button
                            className={styles.billDetailReturnItemsBtn}
                            onClick={() => setSelectedItems([])}
                          >
                            Clear Selection
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={styles.billActions}>
                    {billData.status === 'FRESH' &&
                      billData.final_amount > 0 &&
                      isToday(new Date(billData.bill_date)) && (
                        <button onClick={handleDeleteBill} className={styles.billDetailDeleteBtn}>
                          Delete Bill
                        </button>
                      )}
                  </div>
                </>
              )}

              <div className={styles.billDetailsActionBtn}>
                <button onClick={onClose}>Close</button>
              </div>
            </>
          )}

          {/* ============================================================
           *  VIEW 2: EXCHANGE (inline)
           * ============================================================ */}
          {currentView === 'exchange' && (
            <div className={styles.exchangeContainer}>
              <div className={styles.exchangeTopBar}>
                <button className={styles.backBtn} onClick={handleBackToBill}>
                  ← Back to Bill
                </button>
                <div className={styles.topBarRight}>
                  <input
                    type="text"
                    className={styles.barcodeInputSmall}
                    placeholder="Scan barcode"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    ref={barcodeInputRef}
                    onFocus={() => setIsBarcodeMode(true)}
                  />
                  <button
                    className={`${styles.modeBtn} ${
                      isBarcodeMode ? styles.barcodeModeActive : styles.searchModeActive
                    }`}
                    onClick={toggleBarcodeMode}
                  >
                    {isBarcodeMode ? '📱 Barcode Mode' : '🔍 Search Mode'}
                  </button>
                </div>
              </div>

              <div className={styles.searchSection}>
                <div className={styles.searchContainer}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="🔍 Search by item code"
                    value={searchTerm}
                    ref={searchInputRef}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={handleArrowNavigation}
                  />
                  {dropdownOpen && searchResults.length > 0 && (
                    <div className={styles.dropdown} ref={dropdownRef}>
                      <table className={styles.dropdownTable}>
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Type</th>
                            <th>Color</th>
                            <th>Size</th>
                            <th>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((item, index) => (
                            <tr
                              key={item.itemBarcodeID}
                              onClick={() => addItemToBill(item)}
                              onKeyDown={(e) => handleDropdownRowKeyDown(e, item)}
                              tabIndex="0"
                              className={index === selectedIndex ? styles.selectedRow : ''}
                            >
                              <td>{item.itemCode}</td>
                              <td>{item.itemName}</td>
                              <td>{item.itemType}</td>
                              <td>{item.itemColor}</td>
                              <td>{item.itemSize}</td>
                              <td>₹{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.infoSection}>
                <div className={styles.creditInfo}>
                  <span className={styles.creditLabel}>Credit Available:</span>
                  <span className={styles.creditAmount}>₹{exchangeAmount}</span>
                </div>
                <div className={styles.schoolInfo}>
                  <label className={styles.schoolLabel}>School Name:</label>
                  <Select
                    options={allSchools}
                    ref={selectedSchoolRef}
                    value={allSchools.find((s) => s.schoolName === schoolName) || null}
                    onChange={handleSchoolChange}
                    placeholder="Select a school"
                    styles={customSelectStyles}
                    filterOption={(option, inputValue) =>
                      option.data.schoolName.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.data.schoolCode.toLowerCase().includes(inputValue.toLowerCase())
                    }
                  />
                </div>
              </div>

              <div className={styles.exchangeItemsSection}>
                <h3 className={styles.sectionTitle}>Returned Items (Exchange Credit)</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.exchangeTable}>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Bill Type</th>
                        <th>Item Type</th>
                        <th>School</th>
                        <th>Price</th>
                        <th>Return Qty</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsToExchange.map((item, index) => (
                        <tr key={index}>
                          <td>{item.sno}</td>
                          <td>{item.billCategory}</td>
                          <td>{item.itemType}</td>
                          <td>{item.itemCategory}</td>
                          <td>₹{item.price}</td>
                          <td>{item.return_quantity}</td>
                          <td>₹{item.return_quantity * item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.billingItemsSection}>
                <h3 className={styles.sectionTitle}>Billing Items</h3>
                <div
                  className={styles.itemsTableContainer}
                  onFocus={() => setIsTableFocused(true)}
                  onBlur={() => setIsTableFocused(false)}
                  tabIndex={0}
                >
                  <table className={styles.itemsTable}>
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingItems.map((item, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>{item.itemCode}</td>
                          <td>{item.itemName}</td>
                          <td>{item.itemColor}</td>
                          <td>{item.itemSize}</td>
                          <td>₹{item.price}</td>
                          <td>
                            <input
                              type="number"
                              className={styles.quantityInput}
                              value={item.quantity}
                              ref={(el) => {
                                if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
                                inputRefs.current[rowIndex][4] = el;
                              }}
                              onChange={(e) =>
                                handleBillingQuantityChange(rowIndex, e.target.value)
                              }
                              min="1"
                            />
                          </td>
                          <td>₹{item.amount.toFixed(2)}</td>
                          <td>
                            <button
                              className={styles.removeBtn}
                              onClick={() => removeItemFromBill(rowIndex)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.footer}>
                <div className={styles.customItemSection}>
                  <button
                    className={styles.customItemBtn}
                    onClick={() => setShowCustomItemModal(true)}
                  >
                    + Custom Item
                  </button>
                </div>

                <div className={styles.summarySection}>
                  <div
                    className={`${styles.totalAmount} ${
                      calculateBillingTotal() - exchangeAmount < 0
                        ? styles.negativeAmount
                        : styles.positiveAmount
                    }`}
                  >
                    Net Amount: ₹{(calculateBillingTotal() - exchangeAmount).toFixed(2)}
                  </div>
                  <div className={styles.totalQuantity}>
                    Total Quantity: {calculateBillingQuantity()}
                  </div>
                </div>

                <div className={styles.paymentSection}>
                  <div className={styles.paymentMode}>
                    <label>Payment Mode:</label>
                    <select
                      className={styles.paymentSelect}
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Upi">UPI</option>
                    </select>
                  </div>
                  <button
                    className={styles.billBtn}
                    onClick={handleExchangeSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className={styles.btnContent}>
                        <span className={styles.exchangeLoader}></span>
                        Processing...
                      </span>
                    ) : (
                      'Generate Bill'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* -------------------- Defect modal -------------------- */}
          {isDefectModalOpen && defectItem && (
            <div className={styles.uniqueDefectModal}>
              <div className={styles.uniqueDefectModalContent}>
                <h2>Defect Item</h2>
                <table className={styles.uniqueDefectItemsTable}>
                  <thead>
                    <tr>
                      <th>Barcode ID</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Defect Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{defectItem.itemBarcodeID}</td>
                      <td>{defectItem.itemType}</td>
                      <td>{defectItem.itemCategory}</td>
                      <td>{defectItem.sellPrice}</td>
                      <td>
                        <input
                          type="number"
                          value={defectQuantity}
                          onChange={(e) => {
                            const q = Number(e.target.value);
                            if (q <= defectItem.quantity) setDefectQuantity(q);
                            else {
                              alert(`Defect quantity cannot exceed ${defectItem.quantity}`);
                              setDefectQuantity(1);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className={styles.uniqueDefectAmount}>
                  Amount: {defectItem.sellPrice * defectQuantity}
                </div>
                <div className={styles.uniqueDefectButtons}>
                  <button onClick={confirmDefect}>Confirm Defect</button>
                  <button onClick={() => setIsDefectModalOpen(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* -------------------- Generic popup -------------------- */}
          {showPopup && (
            <Popup message={popupMessage} type={popupType} onClose={handleClosePopup} />
          )}

          {/* -------------------- Bill selection popup -------------------- */}
          {showBillSelectionPopup && (
            <BillSelectionPopup
              list={billList}
              onClose={() => setShowBillSelectionPopup(false)}
              onSelectBill={handleSelectBill}
            />
          )}

          {/* -------------------- Confirm (Ctrl+S) popup -------------------- */}
          {showConfirmPopup && (
            <BillPopup onConfirm={handleConfirmSubmit} onCancel={() => setShowConfirmPopup(false)} />
          )}

          {/* -------------------- Thermal print modal (same as NewBillContainer) -------------------- */}
          <PrintBillModal
            isOpen={showPrintModal}
            onClose={() => setShowPrintModal(false)}
            billInfo={billInfo}
          />

          {/* -------------------- Custom item modal -------------------- */}
          {showCustomItemModal && (
            <CustomItemPopup
              showCustomItemModal={showCustomItemModal}
              setShowCustomItemModal={setShowCustomItemModal}
              customItem={customItem}
              handleCustomItemChange={handleCustomItemChange}
              handleAddCustomItem={handleAddCustomItem}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnExchangePop;